import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Search, Sparkles, Calendar as CalendarIcon, Filter } from "lucide-react";
import { format, startOfDay } from "date-fns";
import { de, enUS } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { DynamicSearchCalendar } from "./DynamicSearchCalendar";
import { CityAutocomplete } from "@/components/ui/CityAutocomplete";
import { GuestSelector, GuestData } from "@/components/ui/GuestSelector";

export const EnhancedSearchForm = () => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'de' ? de : enUS;
  const navigate = useNavigate();
  const [searchData, setSearchData] = useState({
    destination: "",
    checkin: null as Date | null,
    checkout: null as Date | null,
    guests: { adults: 1, children: [] } as GuestData,
    flexible: false,
    cancellableOnly: false
  });
  
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [inspirationMode, setInspirationMode] = useState(false);

  const handleDateSelect = (checkin: Date | null, checkout: Date | null) => {
    setSearchData(prev => ({ ...prev, checkin, checkout }));
    if (checkin && checkout) {
      setIsCalendarOpen(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();

    const toDateParam = (d: Date) => format(startOfDay(d), "yyyy-MM-dd");

    // Build search params
    const params = new URLSearchParams();

    if (searchData.destination) {
      params.set('destination', searchData.destination);
    } else if (!inspirationMode) {
      // Enable inspiration mode if no destination
      setInspirationMode(true);
      params.set('inspiration', 'true');
    }

    // IMPORTANT: Never use toISOString().split('T')[0] here – it converts to UTC and can shift the date by 1 day.
    if (searchData.checkin && !searchData.flexible) {
      params.set('checkin', toDateParam(searchData.checkin));
    }

    if (searchData.checkout && !searchData.flexible) {
      params.set('checkout', toDateParam(searchData.checkout));
    }

    const totalGuests = searchData.guests.adults + searchData.guests.children.length;
    if (totalGuests > 0) {
      params.set('guests', totalGuests.toString());
      params.set('adults', searchData.guests.adults.toString());
      if (searchData.guests.children.length > 0) {
        params.set('children', JSON.stringify(searchData.guests.children));
      }
    }

    if (searchData.flexible) {
      params.set('flexible', 'true');
    }

    if (searchData.cancellableOnly) {
      params.set('cancellable', 'true');
    }

    navigate(`/listings?${params.toString()}`);
  };

  const clearDates = () => {
    setSearchData(prev => ({ ...prev, checkin: null, checkout: null }));
  };

  return (
    <form onSubmit={handleSearch}>
      <Card className="shadow-strong border-border/10">
        <CardContent className="p-6 md:p-8">
          {/* Inspiration Mode Banner */}
          {inspirationMode && (
            <div className="mb-6 p-4 bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg border border-primary/20">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-5 w-5 text-primary" />
                <h3 className="font-semibold text-primary">Inspiration-Modus</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Lass dich inspirieren! Wir zeigen dir besondere Unterkünfte, die zu deinen Wünschen passen.
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setInspirationMode(false)}
                className="mt-2"
              >
                Zurück zur normalen Suche
              </Button>
            </div>
          )}

          {/* Main Search Fields */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-6">
            {/* Destination */}
            <div className="md:col-span-4 space-y-2">
              <Label htmlFor="destination" className="text-sm font-medium">
                {t('search.destination')}
              </Label>
              <CityAutocomplete
                placeholder={t('search.destinationPlaceholder')}
                value={searchData.destination}
                onChange={(value) => {
                  setSearchData({...searchData, destination: value});
                  if (value.toLowerCase().includes('überrasch') || 
                      value.toLowerCase().includes('inspir')) {
                    setInspirationMode(true);
                  }
                }}
              />
            </div>

            {/* Dates */}
            <div className="md:col-span-4 space-y-2">
              <Label className="text-sm font-medium">
                {t('search.when')}
              </Label>
              <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "h-12 w-full justify-start text-left font-normal border-0 bg-muted/30 hover:bg-white transition-smooth",
                      (!searchData.checkin || !searchData.checkout) && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {searchData.checkin && searchData.checkout ? (
                      <span>
                        {format(searchData.checkin, "dd.MM.", { locale: dateLocale })} - {format(searchData.checkout, "dd.MM.yy", { locale: dateLocale })}
                      </span>
                    ) : (
                      <span>{t('search.datePlaceholder')}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <DynamicSearchCalendar
                    onDateSelect={handleDateSelect}
                    selectedCheckin={searchData.checkin}
                    selectedCheckout={searchData.checkout}
                    mode="search"
                  />
                  {(searchData.checkin || searchData.checkout) && (
                    <div className="p-3 border-t">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={clearDates}
                        className="w-full"
                      >
                        Daten löschen
                      </Button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            </div>

            {/* Guests */}
            <div className="md:col-span-4 space-y-2">
              <Label className="text-sm font-medium">
                {t('search.guests')}
              </Label>
              <GuestSelector
                value={searchData.guests}
                onChange={(guests) => setSearchData(prev => ({ ...prev, guests }))}
                compact={true}
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-4 p-4 bg-muted/20 rounded-lg mb-6">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">{t('search.filter')}:</span>
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                id="flexible"
                checked={searchData.flexible}
                onCheckedChange={(checked) => setSearchData({...searchData, flexible: checked})}
              />
              <Label htmlFor="flexible" className="text-sm cursor-pointer">
                {t('search.flexible')}
              </Label>
              {searchData.flexible && (
                <Badge variant="secondary" className="text-xs">
                  ±3 Tage
                </Badge>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="cancellable"
                checked={searchData.cancellableOnly}
                onCheckedChange={(checked) => setSearchData({...searchData, cancellableOnly: checked})}
              />
              <Label htmlFor="cancellable" className="text-sm cursor-pointer">
                {t('search.cancellableOnly')}
              </Label>
            </div>
          </div>

          {/* Search Button */}
          <div className="flex flex-col items-center gap-3">
            <Button 
              type="submit" 
              variant="hero" 
              size="xl" 
              className="w-full md:w-auto px-12"
            >
              <Search className="h-5 w-5 mr-2" />
              {t('search.searchButton')}
            </Button>
            
            {!inspirationMode && (
              <Button
                type="button"
                variant="link"
                onClick={() => setInspirationMode(true)}
                className="text-sm"
              >
                <Sparkles className="h-4 w-4 mr-1" />
                {t('search.inspiration')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </form>
  );
};